import { FC, useState } from 'react';
import {
	Modal,
	ModalContent,
	ModalOverlay,
	ModalCloseButton,
	Text,
	Button,
	VStack,
	Icon,
	Box,
	Badge,
} from '@chakra-ui/react';
import { IoWarningOutline } from 'react-icons/io5';
import { MdArrowUpward, MdPeople } from 'react-icons/md';
import { subscribe, getAppSumoUpgradeUrl } from 'src/services/billing';
import { customToast, toastError } from 'src/services/toast';

interface UserSeatsLimitModalProps {
	isOpen: boolean;
	onClose: () => void;
	currentCount?: number;
	limit?: number;
	provider?: 'appsumo' | 'stripe';
	planName?: string;
	canUpgrade?: boolean;
	upgradePrice?: number;
	upgradeCurrency?: string;
	nextPlanId?: string;
	onUpgradeSuccess?: () => void;
}

const UserSeatsLimitModal: FC<UserSeatsLimitModalProps> = ({
	isOpen,
	onClose,
	limit = 0,
	planName,
	provider = 'stripe',
	canUpgrade = true,
	upgradePrice,
	upgradeCurrency = 'usd',
	nextPlanId,
	onUpgradeSuccess,
}) => {
	const [isUpgrading, setIsUpgrading] = useState(false);

	const handleUpgrade = async () => {
		if (provider === 'appsumo') {
			setIsUpgrading(true);
			try {
				const { upgradeUrl } = await getAppSumoUpgradeUrl();
				window.open(upgradeUrl, '_blank');
			} catch (error) {
				toastError(error);
			} finally {
				setIsUpgrading(false);
			}
			return;
		}

		if (!nextPlanId) {
			window.location.href = '/account/subscription';
			return;
		}

		setIsUpgrading(true);
		try {
			await subscribe(nextPlanId);
			customToast('Plan upgraded successfully!', 'success');
			onClose();
			if (onUpgradeSuccess) {
				onUpgradeSuccess();
			} else {
				window.location.reload();
			}
		} catch (error) {
			toastError(error);
		} finally {
			setIsUpgrading(false);
		}
	};

	// Only show remove members option if limit > 1 (not Individual plan)
	const canRemoveMembers = limit > 1;

	const formatPrice = (amount?: number, currency?: string) => {
		if (!amount) return '';
		const price = amount / 100;
		const currencySymbol = currency?.toLowerCase() === 'usd' ? '$' : currency?.toUpperCase() + ' ';
		return `${currencySymbol}${price}`;
	};

	const getUpgradeButtonText = () => {
		if (provider === 'appsumo') {
			return 'Upgrade on AppSumo';
		}
		if (upgradePrice) {
			return `Upgrade Plan for ${formatPrice(upgradePrice, upgradeCurrency)}/mo`;
		}
		return 'Upgrade Plan';
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} isCentered closeOnOverlayClick={!isUpgrading}>
			<ModalOverlay bg="blackAlpha.600" />
			<ModalContent 
				borderRadius={{ base: 'xl', md: '2xl' }} 
				mx={{ base: 3, md: 4 }} 
				mr={{ base: '70px', md: 4 }}
				maxW={{ base: 'calc(100% - 80px)', md: '520px' }}
				my={{ base: 4, md: 'auto' }}
			>
				<ModalCloseButton isDisabled={isUpgrading} />
				
				<VStack spacing={{ base: 4, md: 6 }} py={{ base: 6, md: 10 }} px={{ base: 4, md: 8 }}>
					<Box
						bg="orange.50"
						p={{ base: 3, md: 5 }}
						borderRadius="full"
					>
						<Icon as={IoWarningOutline} w={{ base: 8, md: 12 }} h={{ base: 8, md: 12 }} color="orange.500" />
					</Box>
					
					<VStack spacing={3} textAlign="center">
						<Badge colorScheme="orange" fontSize="xs" px={2} py={1} borderRadius="md">
							{planName || 'Current'} Plan
						</Badge>
						<Text fontSize={{ base: 'lg', md: '2xl' }} fontWeight="bold" color="gray.800">
							Team Member Limit Reached
						</Text>
						<Text fontSize={{ base: 'sm', md: 'md' }} color="gray.600" lineHeight="tall">
							You've reached the maximum number of team members for your plan.
							To invite more users, please upgrade to a higher plan.
						</Text>
					</VStack>

					<VStack spacing={3} w="full" pt={2}>
						{canUpgrade && (
							<Button
								variant="orangeSolid"
								onClick={handleUpgrade}
								leftIcon={<Icon as={MdArrowUpward} />}
								size={{ base: 'md', md: 'lg' }}
								w="full"
								isLoading={isUpgrading}
								loadingText="Upgrading..."
							>
								{getUpgradeButtonText()}
							</Button>
						)}
						{canRemoveMembers && (
							<Button
								onClick={onClose}
								leftIcon={<Icon as={MdPeople} />}
								size={{ base: 'md', md: 'lg' }}
								w="full"
								variant="outline"
								isDisabled={isUpgrading}
							>
								Remove Team Members
							</Button>
						)}
					</VStack>
				</VStack>
			</ModalContent>
		</Modal>
	);
};

export default UserSeatsLimitModal;
